import { AfterViewInit, Component, Inject, LOCALE_ID, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';

import { MapCoordinates, MapIcons, NotificationFacade } from '@ridango/components';
import { RideBookingsFacade, RideBookingsService } from '@ridango/components/lib/ride-bookings';
import { AppStartupService, OndemandOrderStatus, OndemandRide, WebSocketService, BaseComponent } from '@ridango/core';
import Env from '@ridango/core/env';

import * as PolylineUtil from 'polyline-encoded';
import { finalize, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'gl-bookings-page',
    templateUrl: './account-bookings-page.component.html',
    styleUrls: ['./account-bookings-page.component.scss']
})
export class BookingsPageComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    selectedBooking: OndemandRide = null;

    private cancelConfirmationDialog;
    private bookingRouteDialog;

    @ViewChild('cancelConfirmationModal', { static: true }) cancelConfirmationRef: TemplateRef<any>;
    @ViewChild('bookingRouteModal', { static: true }) bookingRouteModalRef: TemplateRef<any>;

    bookings$ = this.bookingsFacade.bookings$;
    confirmedBookings$ = this.bookings$.pipe(
        map((bookings) => bookings.filter((booking) => booking.status === OndemandOrderStatus.CONFIRMED))
    );
    pendingBookings$ = this.bookings$.pipe(
        map((bookings) => bookings.filter((booking) => booking.status === OndemandOrderStatus.PROCESSING))
    );
    loading$ = this.bookingsFacade.loading$;

    mapCenter: MapCoordinates = this.env.MAP.COORDINATES;
    polylineColor = '#3164ce';
    ride = null;
    rideStart: number[];
    rideStop: number[];
    buses = [];

    dateFormat = this.env.TICKET_CARD_DATE_FORMAT;

    iconOrigin = MapIcons('pin');
    iconDestination = MapIcons('origin');

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        private dialog: MatDialog,
        private bookingsFacade: RideBookingsFacade,
        private bookingsService: RideBookingsService,
        private webSocketService: WebSocketService,
        private notification: NotificationFacade,
        private translate: TranslateService,
        public appStartupService: AppStartupService
    ) {
        super();
    }

    ngOnInit() {
        this.locale = this.translate.currentLang;
    }

    ngAfterViewInit() {
        this.bookingsFacade.fetchRideBookings();
    }

    updateList() {
        this.bookingsFacade.fetchRideBookings();
    }

    showBookingRoute(booking: OndemandRide) {
        this.selectedBooking = booking;
        this.bookingRouteDialog = this.dialog.open(this.bookingRouteModalRef, {
            height: '90vh',
            width: '100vw',
            panelClass: 'map-modal'
        });

        this.bookingRouteDialog
            .afterOpened()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.bookingsService.fetchRideDetails(booking.ondemand_trip_id).subscribe((ride) => {
                    this.initMap(ride);
                });
            });
        this.bookingRouteDialog
            .afterClosed()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.selectedBooking = null;
                this.closeMap();
            });
    }

    showCancelModal(booking: OndemandRide) {
        this.selectedBooking = booking;
        this.cancelConfirmationDialog = this.dialog.open(this.cancelConfirmationRef);
        this.cancelConfirmationDialog
            .afterClosed()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.selectedBooking = null;
            });
    }

    closeCancelModal() {
        this.selectedBooking = null;
        this.cancelConfirmationDialog.close();
    }

    cancelBooking() {
        this.bookingsService
            .cancelRideBooking(this.selectedBooking.id)
            .pipe(
                finalize(() => {
                    this.closeCancelModal();
                    window.scroll(0, 0);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe(
                () => {
                    this.notification.success('BOOKINGS_PAGE_ALERT_CANCEL_SUCCESS');
                    this.updateList();
                },
                () => {
                    this.notification.error('BOOKINGS_PAGE_ALERT_CANCEL_ERROR');
                }
            );
    }

    private initMap(ride): void {
        const latlngs = PolylineUtil.decode(ride.shape_polyline);
        this.rideStart = latlngs[0];
        this.rideStop = latlngs[latlngs.length - 1];
        this.ride = ride;

        this.webSocketService
            .connect('/ws/realtime')
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((buses) => (this.buses = buses));

        const payload = {
            regionId: this.env.REGION_ID,
            topLeftCoordinates: {
                longitude: 0,
                latitude: 180
            },
            bottomRightCoordinates: {
                longitude: 180,
                latitude: 0
            },
            onDemandTripId: ride.id
        };

        this.webSocketService.send(payload);
    }

    private closeMap() {
        this.rideStart = null;
        this.rideStop = null;
        this.ride = null;

        this.webSocketService.closeConnection();
    }
}
